// frontend/src/services/equiposService.js
// SERVICIO PARA INTERACTUAR CON LA API DE EQUIPOS - CORREGIDO
import apiClient from './apiClient';

class EquiposService {
  constructor() {
    this._todosEquiposPromise = null;
    this._todosEquiposCache = null;
  }

  // OBTENER TODAS LAS FAMILIAS DE EQUIPOS (SIN PAGINACIÓN)
  async getFamilias() {
    try {
      // page_size=10000 = máximo del backend: trae TODAS las familias en un
      // solo request (antes hacía un request extra solo para contar).
      const response = await apiClient.get(`/familias/?page_size=10000`);
      const data = response.data.results || response.data;
      return { data };
    } catch (error) {
      console.error('❌ Error al cargar familias:', error);
      throw error;
    }
  }

  // OBTENER TODOS LOS EQUIPOS PARA EL ÁRBOL (SIN PAGINACIÓN)
  async getTodosEquipos(options = {}) {
    const { forceRefresh = false } = options;
    try {
      if (!forceRefresh && this._todosEquiposCache) {
        return { data: this._todosEquiposCache };
      }

      if (!forceRefresh && this._todosEquiposPromise) {
        return this._todosEquiposPromise;
      }

      console.log('📡 Cargando TODOS los equipos para el árbol...');

      const requestConfig = { timeout: 30000 };

      this._todosEquiposPromise = apiClient.get(`/equipos/todos-para-arbol/`, requestConfig)
        .catch(async (error) => {
          // Retry once with a higher timeout when the request is aborted by timeout.
          if (error?.code === 'ECONNABORTED') {
            return apiClient.get(`/equipos/todos-para-arbol/`, { timeout: 60000 });
          }
          throw error;
        })
        .then((response) => {
          const data = response.data.results || response.data;
          const count = response.data.count || data.length;
          this._todosEquiposCache = Array.isArray(data) ? data : [];

          console.log('🔍 Equipos recibidos del servidor:', data.length, 'de', count, 'equipos (todos sin paginación)');

          return { data: this._todosEquiposCache };
        })
        .finally(() => {
          this._todosEquiposPromise = null;
        });

      return this._todosEquiposPromise;
    } catch (error) {
      console.error('❌ Error al cargar todos los equipos:', error);
      throw error;
    }
  }

  invalidateCache() {
    this._todosEquiposCache = null;
    this._todosEquiposPromise = null;
  }

  // ¿Ya hay una copia completa de equipos en memoria? (evita pedir el primer
  // tramo rápido cuando la vuelta al Dashboard puede pintar al instante)
  tieneCacheEquipos() {
    return Boolean(this._todosEquiposCache);
  }

  // PRIMER TRAMO para pintar el árbol al instante mientras getTodosEquipos()
  // completa el dataset en segundo plano. NUNCA reemplaza a la carga completa.
  async getPrimerTramoEquipos(pageSize = 150) {
    const response = await apiClient.get(`/equipos/todos-para-arbol/`, {
      params: { page: 1, page_size: pageSize },
    });
    const data = response.data.results || response.data;
    return { data: Array.isArray(data) ? data : [], count: response.data.count ?? null };
  }

  // OBTENER EQUIPOS CON PAGINACIÓN Y FILTROS
  async getEquipos(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const response = await apiClient.get(`/equipos/?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al cargar equipos:', error);
      throw error;
    }
  }

  // OBTENER DATOS PARA FILTROS (familias, sucursales, estados, tipos, empresas, obras, ubicaciones)
  async getDatosParaFiltros(options = {}) {
    try {
      console.log('📡 Cargando datos para filtros...');

      const {
        includeEmpresas = true,
        includeObras = true,
        includeUbicaciones = true,
        includeCentrosLogisticos = false,
        includeCentrosCosto = true,
      } = options;

      const requests = [
        apiClient.get(`/familias/?page_size=10000`),
        apiClient.get(`/sucursales/?page_size=10000`),
        apiClient.get(`/estados/?page_size=10000`),
        apiClient.get(`/tipos/?page_size=10000`),
        includeEmpresas ? apiClient.get(`/empresas/?page_size=10000`) : Promise.resolve({ data: [] }),
        includeObras ? apiClient.get(`/obras/?page_size=10000`) : Promise.resolve({ data: [] }),
        includeUbicaciones ? apiClient.get(`/ubicaciones/?page_size=10000`) : Promise.resolve({ data: [] }),
        includeCentrosLogisticos ? apiClient.get(`/centros-logisticos/?page_size=10000`) : Promise.resolve({ data: [] }),
        includeCentrosCosto ? apiClient.get(`/centroscosto/?page_size=10000`) : Promise.resolve({ data: [] }),
      ];

      const [familiasRes, sucursalesRes, estadosRes, tiposRes, empresasRes, obrasRes, ubicacionesRes, centrosLogisticosRes, centrosCostoRes] = await Promise.allSettled(requests);

      const asList = (result, label) => {
        if (result.status === 'fulfilled') {
          return result.value?.data?.results || result.value?.data || [];
        }
        console.warn(`⚠️ No se pudieron cargar ${label}:`, result.reason?.response?.status || result.reason?.message || result.reason);
        return [];
      };

      const familias = asList(familiasRes, 'familias');
      const sucursales = asList(sucursalesRes, 'sucursales');
      const estados = asList(estadosRes, 'estados');
      const tipos = asList(tiposRes, 'tipos');
      const empresas = asList(empresasRes, 'empresas');
      const obras = asList(obrasRes, 'obras');
      const ubicaciones = asList(ubicacionesRes, 'ubicaciones');
      const centrosLogisticos = asList(centrosLogisticosRes, 'centros logísticos');
      const centrosCosto = asList(centrosCostoRes, 'centros de costo');

      return { familias, sucursales, estados, tipos, empresas, obras, ubicaciones, centrosLogisticos, centrosCosto };
    } catch (error) {
      console.error('❌ Error al cargar datos para filtros:', error);
      throw error;
    }
  }

  // OBTENER EQUIPOS POR FAMILIA
  async getEquiposPorFamilia(familiaId) {
    try {
      const response = await apiClient.get(`/equipos/?familia=${familiaId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al cargar equipos por familia:', error);
      throw error;
    }
  }

  // OBTENER DETALLE DE EQUIPO
  async getDetalleEquipo(equipoId) {
    try {
      const response = await apiClient.get(`/equipos/${equipoId}/`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al cargar detalle del equipo:', error);
      throw error;
    }
  }

  // BUSCAR EQUIPOS
  async buscarEquipos(query) {
    try {
      const response = await apiClient.get(`/equipos/?search=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al buscar equipos:', error);
      throw error;
    }
  }

  // MÉTODOS PARA CRUD DE EQUIPOS (LISTADO)

  // OBTENER EQUIPOS PARA LISTADO (ALIAS DE getEquipos)
  async obtenerEquipos(filtros = {}) {
    return this.getEquipos(filtros);
  }

  // CREAR NUEVO EQUIPO
  async crearEquipo(datosEquipo) {
    try {
      const response = await apiClient.post('/equipos/', datosEquipo);
      return response.data;
    } catch (error) {
      console.error('❌ Error al crear equipo:', error);
      throw error;
    }
  }

  // ACTUALIZAR EQUIPO EXISTENTE
  async actualizarEquipo(equipoId, datosEquipo) {
    try {
      const response = await apiClient.put(`/equipos/${equipoId}/`, datosEquipo);
      return response.data;
    } catch (error) {
      console.error('❌ Error al actualizar equipo:', error);
      throw error;
    }
  }

  // ELIMINAR EQUIPO
  async eliminarEquipo(equipoId) {
    try {
      const response = await apiClient.delete(`/equipos/${equipoId}/`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al eliminar equipo:', error);
      throw error;
    }
  }

  // EXPORTAR EQUIPOS A EXCEL
  async exportarEquipos(filtros = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.keys(filtros).forEach(key => {
        if (filtros[key] !== null && filtros[key] !== undefined && filtros[key] !== '') {
          queryParams.append(key, filtros[key]);
        }
      });

      const response = await apiClient.get(`/equipos/export/?${queryParams.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error al exportar equipos:', error);
      throw error;
    }
  }

  // MÉTODOS CON NOMBRES ALTERNATIVOS PARA COMPATIBILIDAD

  // Alias para getDetalleEquipo
  async obtenerDetalle(equipoId) {
    return this.getDetalleEquipo(equipoId);
  }

  // Método para obtener mantenimientos de un equipo
  async obtenerMantenimientos(equipoId) {
    try {
      const response = await apiClient.get(`/equipos/${equipoId}/mantenimientos/`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al cargar mantenimientos del equipo:', error);
      throw error;
    }
  }

  // Método para obtener repuestos recomendados de un equipo
  async obtenerRepuestosRecomendados(equipoId) {
    try {
      // El backend expone un ViewSet "repuestos-recomendados" con paginación.
      // Traer todas las páginas para no perder recomendaciones cuando hay más de 25.
      let nextUrl = `/repuestos-recomendados/?equipo=${equipoId}&page_size=1000`;
      let acumulado = [];
      let guard = 0;

      while (nextUrl && guard < 30) {
        const response = await apiClient.get(nextUrl);
        const data = response.data;
        if (Array.isArray(data)) {
          acumulado = acumulado.concat(data);
          nextUrl = null;
        } else {
          const results = Array.isArray(data?.results) ? data.results : [];
          acumulado = acumulado.concat(results);
          nextUrl = data?.next || null;
        }
        guard += 1;
      }

      return acumulado;
    } catch (error) {
      console.error('❌ Error al cargar repuestos recomendados:', error);
      throw error;
    }
  }

  // Método para obtener las capacidades de llenado (fluidos) de un equipo
  async obtenerCapacidadesLlenado(equipoId) {
    try {
      // Traer todas las páginas para no perder capacidades cuando hay más de 25.
      let nextUrl = `/capacidades-llenado/?equipo=${equipoId}&page_size=1000`;
      let acumulado = [];
      let guard = 0;

      while (nextUrl && guard < 30) {
        const response = await apiClient.get(nextUrl);
        const data = response.data;
        if (Array.isArray(data)) {
          acumulado = acumulado.concat(data);
          nextUrl = null;
        } else {
          const results = Array.isArray(data?.results) ? data.results : [];
          acumulado = acumulado.concat(results);
          nextUrl = data?.next || null;
        }
        guard += 1;
      }

      return acumulado;
    } catch (error) {
      console.error('❌ Error al cargar capacidades de llenado:', error);
      throw error;
    }
  }

  // Crear una capacidad de llenado para un equipo
  async crearCapacidadLlenado(payload) {
    const response = await apiClient.post('/capacidades-llenado/', payload);
    return response.data;
  }

  // Actualizar una capacidad de llenado (ej: capacidad_litros)
  async actualizarCapacidadLlenado(id, payload) {
    const response = await apiClient.patch(`/capacidades-llenado/${id}/`, payload);
    return response.data;
  }

  // Eliminar una capacidad de llenado
  async eliminarCapacidadLlenado(id) {
    await apiClient.delete(`/capacidades-llenado/${id}/`);
  }

  // Clonar capacidades de llenado entre equipos.
  // El payload admite un destino (`equipo_destino`) o varios (`equipos_destino`).
  async clonarCapacidadesLlenado(payload) {
    const response = await apiClient.post('/capacidades-llenado/clonar-desde-equipo/', payload);
    return response.data;
  }

  // Clonar repuestos recomendados entre equipos.
  // El payload admite un destino (`equipo_destino`) o varios (`equipos_destino`).
  async clonarRepuestosRecomendados(payload) {
    const response = await apiClient.post('/repuestos-recomendados/clonar-desde-equipo/', payload);
    return response.data;
  }

  // Obtener catálogo de depósitos
  async obtenerDepositos() {
    const response = await apiClient.get('/depositos/?page_size=9999');
    return response.data?.results || response.data || [];
  }

  // ALIAS para compatibilidad con Dashboard - igual que getTodosEquipos
  async getAllEquipos() {
    return this.getTodosEquipos();
  }

  // OBTENER ESTADOS DE EQUIPO (catálogo EstadoEquipo)
  async obtenerEstados() {
    try {
      const response = await apiClient.get('/estados/?page_size=9999');
      return response.data?.results || response.data || [];
    } catch (error) {
      console.error('❌ Error al cargar estados de equipo:', error);
      throw error;
    }
  }

  // ACTUALIZAR SOLO EL ESTADO DE UN EQUIPO (PATCH parcial)
  async actualizarEstado(equipoId, estadoId) {
    try {
      const response = await apiClient.patch(`/equipos/${equipoId}/`, { estado: estadoId });
      return response.data;
    } catch (error) {
      console.error('❌ Error al actualizar estado del equipo:', error);
      throw error;
    }
  }

  // OBTENER OPCIONES PARA FILTROS (alias de getDatosParaFiltros para compatibilidad)
  async getFiltrosOpciones() {
    try {
      console.log('📡 Cargando opciones de filtros...');
      
      // Usar getDatosParaFiltros que ya existe y funciona bien
      const data = await this.getDatosParaFiltros();
      
      // Adaptar estructura legacy para consumidores que esperan objeto con `success` y `data`.
      return {
        success: true,
        data: {
          familias: data.familias || [],
          sucursales: data.sucursales || [],
          estados: data.estados || [],
          tipos: data.tipos || [],
          empresas: data.empresas || [],
          obras: data.obras || [],
          ubicaciones: data.ubicaciones || []
        }
      };
    } catch (error) {
      console.error('❌ Error al cargar opciones de filtros:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Exportar instancia única del servicio
const equiposService = new EquiposService();
export default equiposService;
